import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the ApiKey CRUD page in a tab.
 */
const API_KEY_CRUD_ROUTE = "/home/ApiKeyConfig";

//endregion

//region Component

/**
 * Renders the API Keys configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the ApiKeys MTable;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - keep refresh state local to this config page.
 *
 * @param {object} props - Component props.
 * @param {Array<string|object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} API Keys configuration page.
 */
export default function ApiKeyConfig({ roles, onCreateTab, toastMessages }) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [refresh, setRefresh] = useState(false);

  /**
   * Removes table actions that are not required in this configuration page.
   *
   * Important:
   * - Print is not part of the desired API Keys workflow;
   * - Copy is disabled to keep API key creation explicit.
   */
  const removeContextItems = useMemo(() => ["Print", "Copy"], []);

  /**
   * Opens the API Key CRUD tab for the requested operation.
   *
   * Flow:
   * - persists the operation in M_State;
   * - validates row selection for View/Edit/Copy;
   * - persists the selected API key row when required;
   * - opens the configured CRUD route with a contextual tab label.
   *
   * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
   * @param {object|null|undefined} row - Selected API key row.
   * @returns {void}
   */
  const openApiKeyCrud = useCallback(
    (operation, row) => {
      dispatch(
        M_StateActions.addM_State({
          key: "operation",
          value: operation,
        })
      );

      if (["Edit", "Copy", "View"].includes(operation)) {
        if (!row) {
          toastMessages("error", "", t("SelectedLine"));
          return;
        }

        dispatch(
          M_StateActions.addM_State({
            key: "ApiKeyLine",
            value: row,
          })
        );
      }

      const tabLabel =
        operation === "Create"
          ? t("LabelNewApiKey")
          : operation === "View"
            ? `${t("LabelViewApiKey")} "${row?.Name ?? ""}"`
            : `${t("LabelEditApiKey")} "${row?.Name ?? ""}"`;

      onCreateTab(API_KEY_CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  /**
   * Replaces MTable default CRUD handlers with the tab-based API Key workflow.
   */
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ["View", (row) => openApiKeyCrud("View", row)],
        ["Create", () => openApiKeyCrud("Create")],
        ["Edit", (row) => openApiKeyCrud("Edit", row)],
      ]),
    [openApiKeyCrud]
  );

  return (
    <div
      className="config-page"
      style={{ "--config-page-title-color": ctx.secondaryColor }}
    >
      <div className="config-page__header">
        <div className="config-page__title">
          {t("ApiKeys")}
        </div>

        <div className="config-page__description">
          {t("InfoApiKeys")}
        </div>
      </div>

      <div className="config-page__table">
        <MTable
          filtersOnTable
          entityName="ApiKeys"
          roleName="Accounts"
          rolePermissions={roles}
          removeContextItems={removeContextItems}
          refresh={refresh}
          setRefresh={setRefresh}
          useSessionStorage={false}
          replaceFunctions={replaceFunctions}
        />
      </div>
    </div>
  );
}

//endregion

//region PropTypes

ApiKeyConfig.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};

//endregion